import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  Fade,
  SelectChangeEvent,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';

// Import custom hooks
import { useFinancialData } from '@features/ocr/hooks/useFinancialData';
import { useEditableData } from '@features/ocr/hooks/useEditableData';

// Import components
import ErrorBoundary from '@features/ocr/components/ErrorBoundary';
import DocumentTypeTabs from '@features/ocr/components/DocumentTypeTabs';
import DeleteConfirmationDialog from '@features/ocr/components/DeleteConfirmationDialog';
import AddDocTypeDialog from '@features/ocr/components/AddDocTypeDialog';
// Import document type components
import DocumentTypeComponents from '@features/ocr/components/DocumentTypes/index';

// Import types
import {

  Hotel,
  HotelOCRResultsProps,
  FinancialGridRow,
  Category,
  ADRMetricsData,
  OccupancyMetricsData,
  RevPARMetricsData,
  FinancialDataAPI
} from '@features/ocr/types/types';

// Import API service
import { api } from '@services';

// Define our own value formatter params interface for use with DataGrid
interface ValueFormatterParams {
  value: unknown;
  field: string;
  id: string | number;
}

// Define a common props interface with all properties needed by any document type
interface CommonDocumentTypeProps {
  financialData: FinancialDataAPI | null;
  gridRows: FinancialGridRow[];
  gridColumns: GridColDef[];
  addFormCategory: number | '';
  addFormItemName: string;
  addFormYear: string;
  addFormAmount: string;
  saving: boolean;
  loading: boolean;
  selectedHotel: string;
  setError: (message: string | null) => void;
  setAddFormCategory: (value: number | '') => void;
  setAddFormItemName: (value: string) => void;
  setAddFormYear: (value: string) => void;
  setAddFormAmount: (value: string) => void;
  handleAddNewItem: () => Promise<void>;
  handleDeleteClick: (row: FinancialGridRow) => void;
  processRowUpdate: (newRow: FinancialGridRow, oldRow: FinancialGridRow) => Promise<FinancialGridRow>;
  onProcessRowUpdateError: (error: Error) => void;
  fetchADRMetricsData: (hotelId: string) => Promise<void>;
  fetchOccupancyMetricsData: (hotelId: string) => Promise<void>;
  fetchRevPARMetricsData: (hotelId: string) => Promise<void>;
  adrMetricsData: ADRMetricsData | null;
  occupancyMetricsData: OccupancyMetricsData | null;
  revparMetricsData: RevPARMetricsData | null;
  handleBatchDelete: (ids: string[]) => void;
}

const HotelOCRResults: React.FC<HotelOCRResultsProps> = ({ hotelId: initialHotelId }) => {
  // State for hotels
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<string>(initialHotelId || '');

  // State for handling new document type dialog
  const [newDocTypeDialogOpen, setNewDocTypeDialogOpen] = useState<boolean>(false);
  const [newDocTypeName, setNewDocTypeName] = useState<string>('');

  // Use our custom hooks
  const {
    financialData,
    adrMetricsData,
    occupancyMetricsData,
    revparMetricsData,
    loading,
    error,
    success,
    availableDocTypes,
    selectedDocType,
    availableDocTypeOptions,
    fetchFinancialData,
    fetchADRMetricsData,
    fetchOccupancyMetricsData,
    fetchRevPARMetricsData,
    setSelectedDocType,
    setSuccess,
    setError,
  } = useFinancialData();

  const {
    gridRows,
    editedRows,
    deletedRowIds,
    addFormCategory,
    addFormItemName,
    addFormYear,
    addFormAmount,
    saving,
    rowToDelete,
    deleteConfirmOpen,
    setGridRows,
    setEditedRows,
    setDeletedRowIds,
    setAddFormCategory,
    setAddFormItemName,
    setAddFormYear,
    setAddFormAmount,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    processRowUpdate2Arg,
    onProcessRowUpdateError,
    handleSaveAllChanges,
    handleAddNewItem,
    updateGridRowsFromFinancialData,
    hasChanges
  } = useEditableData();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Create grid columns using useMemo
  const gridColumns = useMemo<GridColDef[]>(() => {
    if (!financialData) return [];

    const categoryOptions = financialData.categories.map((cat: Category) => ({
      value: cat.id,
      label: cat.name,
      group: cat.category_group,
    }));

    // Base columns that appear in all document types
    const columns: GridColDef[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 40,
        maxWidth: 40,
        resizable: false,
        cellClassName: 'actions',
        getActions: ({ row }: { row: FinancialGridRow }) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteClick(row)}
            color="inherit"
            sx={{ color: 'error.main' }}
          />,
        ],
      },
      {
        field: 'categoryId',
        headerName: 'Category',
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        flex: 0.8,
        editable: true,
        type: 'singleSelect',
        valueOptions: categoryOptions,
        valueFormatter: (params: ValueFormatterParams) => {
          const categoryId = params.value;
          if (categoryId == null) return '';
          const category = categoryOptions.find((c) => c.value === categoryId);
          return category?.label || '';
        },
      },
      {
        field: 'itemName',
        headerName: 'Item',
        width: 150,
        flex: 1,
        minWidth: 120,
        maxWidth: 250,
        editable: true,
      }
    ];

    // Add year columns
    if (financialData.years) {
      financialData.years.forEach((year: string) => {
        const columnConfig: GridColDef = {
          field: year,
          headerName: year,
          width: 100,
          maxWidth: 120,
          minWidth: 70,
          flex: 0.5,
          type: 'number',
          editable: true,
          align: 'right',
          headerAlign: 'right',
          renderCell: (params: GridRenderCellParams) => (
            <Box sx={{
              width: '100%',
              textAlign: 'right',
              pr: 1,
              overflow: 'visible'
            }}>
              {params.formattedValue}
            </Box>
          )
        };

        // Add formatter based on document type
        if (selectedDocType === 'STR Report' || selectedDocType === 'Occupancy Metrics') {
          columnConfig.valueFormatter = (params: ValueFormatterParams) => {
            if (params.value == null || isNaN(Number(params.value))) return '0%';
            return `${Number(params.value).toFixed(1)}%`;
          };
        } else if (
          selectedDocType === 'ADR Metrics' ||
          selectedDocType === 'RevPAR Metrics' ||
          selectedDocType === 'Profit & Loss Statement'
        ) {
          columnConfig.valueFormatter = (params: ValueFormatterParams) => {
            if (params.value == null || isNaN(Number(params.value))) return '$0';
            return Number(params.value).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });
          };
        } else {
          columnConfig.valueFormatter = (params: ValueFormatterParams) => {
            if (params.value == null || isNaN(Number(params.value))) return '0';
            return Number(params.value).toLocaleString();
          };
        }

        columns.push(columnConfig);
      });
    }

    return columns;
  }, [financialData, selectedDocType, handleDeleteClick]);

  // Fetch hotels on initial mount
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        setError(null);
        const response = await api.get('/hotels');
        setHotels(response.data);
        // If initialHotelId is provided and valid, fetch its data
        if (initialHotelId && response.data.some((h: Hotel) => h.id === initialHotelId)) {
          fetchFinancialData(initialHotelId);
        } else if (initialHotelId) {
          setError(`Hotel with ID ${initialHotelId} not found.`);
          setSelectedHotel(''); // Clear selection if invalid ID passed
        }
      } catch (err) {
        console.error('Error fetching hotels:', err);
        setError('Failed to load hotels list.');
      }
    };
    fetchHotels();
  }, [initialHotelId]);

  // Fetch data when selected hotel changes
  useEffect(() => {
    if (selectedHotel) {
      // Fetch financial data
      fetchFinancialData(selectedHotel);
      // Also fetch metrics data to determine which tabs should be available
      fetchADRMetricsData(selectedHotel);
      fetchOccupancyMetricsData(selectedHotel);
      fetchRevPARMetricsData(selectedHotel);
    } else {
      // Clear data if no hotel is selected
      setGridRows([]);
      setEditedRows({});
      setDeletedRowIds(new Set());
    }
  }, [selectedHotel]);

  // Effect to update grid rows when financial data changes
  useEffect(() => {
    if (financialData) {
      // Use the helper function to transform financial data to grid rows
      // This ensures that document type is properly set on each row
      updateGridRowsFromFinancialData(financialData);
    }
  }, [financialData]);

  // Update useEffect to update grid rows when document type changes
  useEffect(() => {
    if (!selectedHotel || !selectedDocType) return;

    // Only fetch specialized data if it's not already loaded
    if (selectedDocType === 'ADR Metrics' && !adrMetricsData) {
      fetchADRMetricsData(selectedHotel);
    } else if (selectedDocType === 'Occupancy Metrics' && !occupancyMetricsData) {
      fetchOccupancyMetricsData(selectedHotel);
    } else if (selectedDocType === 'RevPAR Metrics' && !revparMetricsData) {
      fetchRevPARMetricsData(selectedHotel);
    }

    console.log('Selected document type changed to:', selectedDocType);
  }, [selectedHotel, selectedDocType]);

  // Handle document type change
  const handleDocTypeChange = (docType: string) => {
    console.log('Document type changed to:', docType);

    if (!selectedHotel) return;
    if (docType === selectedDocType) return;

    const previousDocType = selectedDocType;
    console.log('Previous doc type:', previousDocType, 'New doc type:', docType);

    // Update document type
    setSelectedDocType(docType);

    console.log('Current grid rows:', gridRows.length, 'Document types:', [...new Set(gridRows.map(row => row.documentType) || [])]);

    // For P&L, only fetch if we don't have data
    if (docType === 'Profit & Loss Statement' && !gridRows.some(r => r.documentType === 'Profit & Loss Statement')) {
      void fetchFinancialData(selectedHotel);
    }
  };

  // Handler for creating a new document type
  const handleAddNewDocType = async () => {
    if (!newDocTypeName) {
      setError('Please select a document type.');
      return;
    }

    // Check if document type already exists
    if (availableDocTypes.includes(newDocTypeName)) {
      setError(`Document type "${newDocTypeName}" already exists.`);
      return;
    }

    if (!selectedHotel) {
      setError('Please select a hotel first.');
      return;
    }

    try {
      // Create initial empty data structure for the new document type
      const response = await api.post(`/ocr/hotel/${selectedHotel}/add-document-type`, {
        documentType: newDocTypeName
      });

      if (response.status === 200 || response.status === 201) {
        // Add the new document type and set it as selected
        setSelectedDocType(newDocTypeName);

        // Close the dialog and show success message
        setNewDocTypeDialogOpen(false);
        setNewDocTypeName('');
        setSuccess(`Added new document type: ${newDocTypeName}`);

        // Refresh data to ensure we have the updated structure
        await fetchFinancialData(selectedHotel);
      } else {
        throw new Error('Failed to add document type');
      }
    } catch (err: unknown) {
      console.error('Error adding document type:', err);

      // Type guard to check if error is an object with response property
      type ErrorWithResponse = {
        response?: {
          status?: number;
          data?: {
            details?: string;
          }
        }
        message?: string;
      };

      const errorObj = err as ErrorWithResponse;

      // Check for different error types to provide better error messages
      if (errorObj.response && errorObj.response.status === 404) {
        setError('Document type creation is not supported by the server. Please upload a document of this type instead.');
      } else if (errorObj.response && errorObj.response.data && errorObj.response.data.details) {
        setError(`Failed to add document type: ${errorObj.response.data.details}`);
      } else {
        setError(errorObj.message || 'Failed to add document type. Please try uploading a document of this type instead.');
      }
    }
  };

  // Function to save all changes that calls the hook implementation
  const saveAllChanges = async () => {
    await handleSaveAllChanges(
      selectedHotel,
      financialData,
      selectedDocType,
      setSuccess,
      setError,
      fetchFinancialData
    );
  };

  // Function to add new item that calls the hook implementation
  const addNewItem = async () => {
    await handleAddNewItem(
      selectedHotel,
      financialData,
      selectedDocType,
      setSuccess,
      setError,
      fetchFinancialData,
      fetchADRMetricsData,
      fetchOccupancyMetricsData,
      fetchRevPARMetricsData
    );
  };

  // Batch delete handler for Profit & Loss Statement
  const handleBatchDelete = async (ids: string[]) => {
    console.log('handleBatchDelete called with:', { ids, selectedHotel, selectedDocType });

    try {
      // Get the rows to be deleted
      const rowsToDelete = gridRows.filter(row => ids.includes(row.id));
      console.log('Rows to delete:', rowsToDelete);

      // Log the first row structure to understand the data format
      if (rowsToDelete.length > 0) {
        console.log('First row structure:', Object.keys(rowsToDelete[0]));
        console.log('First row data:', rowsToDelete[0]);
      }

      // Group rows by document type since different document types need separate API calls
      const rowsByDocType = rowsToDelete.reduce((acc, row) => {
        const docType = row.documentType || selectedDocType;
        if (!acc[docType]) {
          acc[docType] = [];
        }
        acc[docType].push(row);
        return acc;
      }, {} as Record<string, typeof rowsToDelete>);

      console.log('Rows grouped by document type:', Object.keys(rowsByDocType));

      // Process each document type separately
      let totalDeleted = 0;
      for (const [docType, rows] of Object.entries(rowsByDocType)) {
        // Format the items for deletion using original values if available
        const itemsToDelete = rows.map(row => ({
          category_id: row.originalCategoryId || row.categoryId,
          item_name: row.originalItemName || row.itemName
        }));

        console.log(`Deleting ${itemsToDelete.length} items for document type: ${docType}`, itemsToDelete);

        const requestPayload = {
          items: itemsToDelete,
          documentType: docType // Use the actual document type from the rows
        };
        console.log('Sending delete request with payload:', requestPayload);

        const response = await api.delete(`/ocr/hotel/${selectedHotel}/financials`, {
          data: requestPayload
        });

        console.log(`Delete response for ${docType}:`, response);

        if (response.status >= 200 && response.status < 300) {
          totalDeleted += rows.length;
        }
      }

      if (totalDeleted > 0) {
        // Update local state after successful deletion
        setGridRows(prevRows => prevRows.filter(row => !ids.includes(row.id)));
        setSuccess(`Successfully deleted ${totalDeleted} items`);

        // Add a delay before refreshing to ensure server has processed the deletion
        setTimeout(async () => {
          await fetchFinancialData(selectedHotel);
        }, 1000); // 1 second delay
      }
    } catch (error) {
      console.error('Error deleting rows:', error);
      setError('Failed to delete rows. Please try again.');
    }
  };

  // Prepare common props for document type components
  const commonProps: CommonDocumentTypeProps & { hasChanges?: boolean; handleSaveAllChanges?: typeof handleSaveAllChanges } = {
    financialData,
    gridRows,
    gridColumns,
    addFormCategory,
    addFormItemName,
    addFormYear,
    addFormAmount,
    saving,
    loading,
    selectedHotel,
    setError,
    setAddFormCategory,
    setAddFormItemName,
    setAddFormYear,
    setAddFormAmount,
    handleAddNewItem: addNewItem,
    handleDeleteClick,
    processRowUpdate: processRowUpdate2Arg,
    onProcessRowUpdateError,
    fetchADRMetricsData,
    fetchOccupancyMetricsData,
    fetchRevPARMetricsData,
    adrMetricsData,
    occupancyMetricsData,
    revparMetricsData,
    handleBatchDelete,
    hasChanges,
    handleSaveAllChanges
  };

  // Modify the renderDocumentTypeComponent function to use type-safe assertions
  const renderDocumentTypeComponent = () => {
    if (!selectedDocType || !(selectedDocType in DocumentTypeComponents)) {
      // Default component for document types without a specific implementation
      return (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6">
            {selectedDocType || 'No document type selected'}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This document type does not have a specialized view yet.
            You can still add, edit, and view data using the standard interface below.
          </Alert>

          {/* Add standard form and grid components here */}
          <Box mt={3}>
            {/* Standard add form */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 2,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Add New Item
              </Typography>
              {/* Form fields would go here - similar to the document-specific components */}
            </Paper>

            {/* Standard data grid */}
            <Paper
              sx={{
                flexGrow: 1,
                height: 'calc(100vh - 500px)',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                mb: 2,
              }}
            >
              {/* DataGrid would go here */}
            </Paper>
          </Box>
        </Paper>
      );
    }

    // Get the appropriate component based on document type
    const DocumentTypeComponent = DocumentTypeComponents[selectedDocType as keyof typeof DocumentTypeComponents];

    // Use type assertion to handle the case where TypeScript can't verify component prop compatibility
    // Different document type components have slightly different prop requirements,
    // but at runtime they all work with the common props we're providing.
    // Using 'any' here avoids complex type declarations while ensuring correct runtime behavior.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <DocumentTypeComponent {...(commonProps as any)} />;
  };

  return (
    <ErrorBoundary>
      <Box
        component="div"
        className="ocr-results-container"
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: { xs: 2, sm: 3 },
          height: 'auto',
          minHeight: 'calc(100vh - 64px)',
          boxSizing: 'border-box',
          bgcolor: 'background.default',
          overflow: 'visible',
          '& > *': {
            maxWidth: '100%'
          }
        }}
      >
        {/* --- Header --- */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DescriptionIcon fontSize="large" sx={{ color: 'text.primary' }} />
          <Typography
            variant={isSmallScreen ? 'h5' : 'h4'}
            component="h1"
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            Financial Data Analysis
          </Typography>
        </Box>

        {/* --- Hotel Selector (if no initialHotelId provided) --- */}
        {!initialHotelId && (
          <FormControl fullWidth variant="outlined">
            <InputLabel id="hotel-select-label">Select Hotel</InputLabel>
            <Select
              labelId="hotel-select-label"
              label="Select Hotel"
              value={selectedHotel}
              onChange={(e: SelectChangeEvent<string>) => setSelectedHotel(e.target.value)}
            >
              <MenuItem value="" disabled>
                <em>Select a hotel...</em>
              </MenuItem>
              {hotels.map((hotel) => (
                <MenuItem key={hotel.id} value={hotel.id}>
                  {hotel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* --- Alerts --- */}
        {error && (
          <Fade in={!!error}>
            <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          </Fade>
        )}
        {success && (
          <Fade in={!!success}>
            <Alert severity="success" variant="filled" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
              {success}
            </Alert>
          </Fade>
        )}

        {/* --- Loading Indicator --- */}
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {/* --- Main Content --- */}
        {!loading && selectedHotel && financialData && (
          <>
            {/* Document Type Tabs */}
            <DocumentTypeTabs
              availableDocTypes={availableDocTypes}
              selectedDocType={selectedDocType}
              onDocTypeChange={handleDocTypeChange}
              onAddDocTypeClick={() => setNewDocTypeDialogOpen(true)}
              availableDocTypeOptions={availableDocTypeOptions}
            />

            {/* Document Type Specific Content - Wrap in flex box to allow it to grow */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              overflow: 'visible',
              mb: 6 // Add margin bottom to ensure content doesn't get cut off by the footer
            }}>
              {renderDocumentTypeComponent()}
            </Box>

            {/* --- Save Action Footer --- */}
            <Box
              sx={{
                position: 'fixed', // Changed from 'sticky' to 'fixed'
                bottom: 0,
                left: 0,
                right: 0,
                py: 1.5, // Reduced from 2
                px: 3,
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 'auto',
                boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 10, // Increased from 2
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={saveAllChanges}
                disabled={loading || saving || !selectedDocType || (Object.keys(editedRows).length === 0 && deletedRowIds.size === 0)}
                startIcon={<SaveIcon />}
                size="large"
                sx={{
                  minWidth: 180,
                  height: 48,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                {saving ? 'Saving...' : 'SAVE ALL CHANGES'}
              </Button>
            </Box>
          </>
        )}

        {/* --- Initial Prompt --- */}
        {!loading && !selectedHotel && !initialHotelId && (
          <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
            Please select a hotel to view or edit its financial data.
          </Typography>
        )}
        {!loading && selectedHotel && !financialData && !error && (
          <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
            No financial data found for the selected hotel.
          </Typography>
        )}
        {!loading && selectedHotel && financialData && availableDocTypes.length === 0 && (
          <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
            No document types available. Please upload some financial documents first.
          </Typography>
        )}

        {/* --- Delete Confirmation Dialog --- */}
        <DeleteConfirmationDialog
          open={deleteConfirmOpen}
          rowToDelete={rowToDelete}
          categories={financialData?.categories || []}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />

        {/* Add New Document Type Dialog */}
        <AddDocTypeDialog
          open={newDocTypeDialogOpen}
          newDocTypeName={newDocTypeName}
          onDocTypeChange={setNewDocTypeName}
          onConfirm={handleAddNewDocType}
          onCancel={() => setNewDocTypeDialogOpen(false)}
          availableDocTypeOptions={availableDocTypeOptions}
          loading={loading}
        />
      </Box>
    </ErrorBoundary>
  );
};

export default HotelOCRResults;